import React, { useCallback, useEffect, useState, useRef, Fragment, Component } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents, Circle, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { JobHoverTooltip } from '../jobs/JobHoverTooltip';
import { PackageHoverTooltip } from '../opportunities/PackageHoverTooltip';
import { PropertyHoverTooltip } from '../opportunities/PropertyHoverTooltip';
import { EmergencyHoverTooltip, EmergencyIntervention } from '../opportunities/EmergencyHoverTooltip';
// --- Types ---
export interface Job {
  id: number;
  timeStart: string;
  timeEnd: string;
  address: string;
  city: string;
  host: string;
  type: 'Pulizia Standard' | 'Pulizia Profonda' | 'Check-out';
  cleaner: {
    name: string;
    avatarColor: string;
    initials: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'issue';
  earnings: string;
  lat: number;
  lng: number;
  cleanerId?: string;
}
export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  color: string;
  textColor: string;
  currentLat: number;
  currentLng: number;
}
export interface Property {
  id: number;
  lat: number;
  lng: number;
  address: string;
  isIncluded?: boolean;
  mq: number;
  rooms: number;
  bathrooms: number;
  timeWindow: string;
  tags: string[];
}
export interface PackageData {
  id: number;
  lat: number;
  lng: number;
  zoneName: string;
  count: number;
  properties: Property[];
  name: string;
  baseEarnings: number;
}
interface UnifiedMapProps {
  // Data
  jobs: Job[];
  packages: PackageData[];
  teamMembers: TeamMember[];
  emergencyInterventions: EmergencyIntervention[];
  // Selection State
  activeJobId: number | null;
  activePackageId: number | null;
  selectedMemberId: string | null;
  selectedEmergencyId: number | null;
  // Handlers
  onJobSelect: (id: number) => void;
  onPackageSelect: (id: number) => void;
  onMemberSelect: (id: string | null) => void;
  onEmergencyClick: (id: number) => void;
  onEmergencyPositionUpdate?: (pos: {
    x: number;
    y: number;
  } | null) => void;
  // Visibility Flags
  showJobs: boolean;
  showOpportunities: boolean;
  showTeam: boolean;
}
// --- Constants ---
const SPLIT_ZOOM_THRESHOLD = 15;
// --- Map Helpers ---
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 200);
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}
function ZoomTracker({
  onZoomChange
}: {
  onZoomChange: (zoom: number) => void;
}) {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom())
  });
  useEffect(() => onZoomChange(map.getZoom()), []);
  return null;
}
function MapMovementTracker({
  onMovingChange
}: {
  onMovingChange: (moving: boolean) => void;
}) {
  const map = useMapEvents({
    movestart: () => onMovingChange(true),
    zoomstart: () => onMovingChange(true),
    moveend: () => onMovingChange(false),
    zoomend: () => onMovingChange(false)
  });
  return null;
}
// --- Icon Factories ---
function createHeadquarterIcon(): L.DivIcon {
  const size = 40;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div style="
        width: ${size}px; height: ${size}px;
        background: #1E365C; color: #ffffff;
        border: 3px solid #ffffff;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 12px rgba(30,54,92,0.3);
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
          <path d="M9 22v-4h6v4"></path>
          <path d="M8 6h.01"></path>
          <path d="M16 6h.01"></path>
          <path d="M8 10h.01"></path>
          <path d="M16 10h.01"></path>
          <path d="M8 14h.01"></path>
          <path d="M16 14h.01"></path>
        </svg>
      </div>
      <div style="
        position: absolute;
        bottom: -18px;
        left: 50%;
        transform: translateX(-50%);
        background: #ffffff;
        color: #1E365C;
        padding: 2px 6px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 700;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        white-space: nowrap;
      ">Sede</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createJobMarkerIcon(status: Job['status'], isActive: boolean): L.DivIcon {
  const size = isActive ? 38 : 32;
  let iconColor: string;
  let iconSvg: string;
  switch (status) {
    case 'in-progress':
      iconColor = '#3B82F6';
      iconSvg = '<polygon points="10 7 17 12 10 17" fill="${c}" stroke="${c}" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>';
      break;
    case 'completed':
      iconColor = '#22C55E';
      iconSvg = '<polyline points="6 12 10 16 18 8" stroke="${c}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
      break;
    case 'issue':
      iconColor = '#F59E0B';
      iconSvg = '<line x1="12" y1="8" x2="12" y2="13" stroke="${c}" stroke-width="2.5" stroke-linecap="round"/><circle cx="12" cy="17" r="1" fill="${c}"/>';
      break;
    default:
      iconColor = '#6B7280';
      iconSvg = '<circle cx="12" cy="12" r="7" stroke="${c}" stroke-width="2" fill="none"/><polyline points="12 9 12 12 14.5 13.5" stroke="${c}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
  }
  iconSvg = iconSvg.replace(/\$\{c\}/g, iconColor);
  const borderWidth = isActive ? '3.5px' : '2.5px';
  const shadow = isActive ? '0 2px 8px rgba(0,0,0,0.15)' : `0 2px 6px ${iconColor}33`;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div style="
        position: relative;
        width: ${size}px; height: ${size}px;
        background: #ffffff;
        border: ${borderWidth} solid ${iconColor};
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: ${shadow};
        cursor: pointer;
        transition: all 0.2s ease;
      ">
        <svg width="${size * 0.48}" height="${size * 0.48}" viewBox="0 0 24 24" fill="none">
          ${iconSvg}
        </svg>
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createTeamMemberIcon(member: TeamMember, isSelected: boolean): L.DivIcon {
  const size = isSelected ? 42 : 36;
  const firstName = member.name.split(' ')[0];
  const bgColor = '#1E365C';
  const border = isSelected ? '3px solid #ffffff' : '2.5px solid #ffffff';
  const shadow = isSelected ? '0 0 0 3px rgba(46, 140, 126, 0.35), 0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.18)';
  const userIconSvg = `
    <svg width="${size * 0.48}" height="${size * 0.48}" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  `;
  const labelBorder = isSelected ? '#2E8C7E' : '#1E365C';
  const labelText = isSelected ? '#2E8C7E' : '#1E365C';
  const nameLabel = `
    <div style="
      position: absolute;
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%);
      background: #ffffff;
      color: ${labelText};
      border: 1.5px solid ${labelBorder};
      padding: 1px 6px;
      border-radius: 999px;
      font-size: 9px;
      font-weight: 600;
      font-family: Inter, sans-serif;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      white-space: nowrap;
      line-height: 14px;
    ">${firstName}</div>
  `;
  const totalHeight = size + 14;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${totalHeight}px;">
      <div style="
        width: ${size}px; height: ${size}px;
        background: ${bgColor};
        border: ${border};
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: ${shadow};
        transition: all 0.2s ease;
      ">
        ${userIconSvg}
      </div>
      ${nameLabel}
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, totalHeight],
    iconAnchor: [size / 2, size / 2]
  });
}
function createZoneIcon(count: number, isActive: boolean): L.DivIcon {
  const size = isActive ? 44 : 34;
  const bg = isActive ? '#2E8C7E' : '#ffffff';
  const color = isActive ? '#ffffff' : '#1E365C';
  const border = isActive ? '4px solid rgba(46,140,126,0.25)' : '2px solid #d1d5db';
  const shadow = isActive ? '0 4px 12px rgba(46,140,126,0.35)' : '0 2px 6px rgba(0,0,0,0.12)';
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: ${bg}; color: ${color};
      border: ${border}; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; font-family: Inter, sans-serif;
      box-shadow: ${shadow}; cursor: pointer;
    ">${count}</div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createPropertyDotIcon(index: number): L.DivIcon {
  const size = 28;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: #2E8C7E; color: #ffffff;
      border: 2px solid #ffffff;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 11px; font-family: Inter, sans-serif;
      box-shadow: 0 2px 8px rgba(46,140,126,0.4);
      cursor: pointer;
    ">${index}</div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createEmergencyIcon(): L.DivIcon {
  const size = 34;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div class="pulse-ring"></div>
      <div style="
        position: relative;
        width: ${size}px; height: ${size}px;
        background: #ef4444; color: #ffffff;
        border: 2px solid #ffffff;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-weight: 800; font-size: 18px; font-family: Inter, sans-serif;
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        cursor: pointer;
        z-index: 10;
      ">!</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// --- Geo Helpers ---
function computeBoundingCircle(properties: Property[], centerLat: number, centerLng: number): number {
  if (properties.length === 0) return 200;
  const toRad = (deg: number) => deg * Math.PI / 180;
  let maxDist = 0;
  for (const p of properties) {
    const dLat = toRad(p.lat - centerLat);
    const dLng = toRad(p.lng - centerLng);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(centerLat)) * Math.cos(toRad(p.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = 6371000 * c;
    if (dist > maxDist) maxDist = dist;
  }
  return Math.max(maxDist * 1.3, 150);
}
function computePackageBounds(pkg: PackageData): L.LatLngBounds {
  const lats = pkg.properties.map(p => p.lat);
  const lngs = pkg.properties.map(p => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latPad = Math.max((maxLat - minLat) * 0.4, 0.002);
  const lngPad = Math.max((maxLng - minLng) * 0.4, 0.002);
  return L.latLngBounds([minLat - latPad, minLng - lngPad], [maxLat + latPad, maxLng + lngPad]);
}
// --- Map Controller ---
function MapController({
  center,
  zoom,
  activePackage,
  activeJob
}: {
  center: [number, number];
  zoom: number;
  activePackage: PackageData | null;
  activeJob: Job | null;
}) {
  const map = useMap();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      map.setView(center, zoom);
      return;
    }
    // Priority: Package Bounds > Job Location > Default Center
    if (activePackage && activePackage.properties.length > 0) {
      const bounds = computePackageBounds(activePackage);
      map.flyToBounds(bounds, {
        padding: [60, 60],
        paddingTopLeft: [420, 60],
        maxZoom: 18,
        duration: 1.5
      });
    } else if (activeJob) {
      map.flyTo([activeJob.lat, activeJob.lng], 16, {
        duration: 1.5
      });
    } else {
      map.flyTo(center, zoom, {
        duration: 1.2
      });
    }
  }, [center[0], center[1], zoom, activePackage?.id, activeJob?.id]);
  return null;
}
// --- Tooltip Position Updater ---
function TooltipPositionUpdater({
  hoveredPackage,
  hoveredEmergency,
  hoveredProperty,
  hoveredJob,
  onPositionUpdate
}: {
  hoveredPackage: PackageData | null;
  hoveredEmergency: EmergencyIntervention | null;
  hoveredProperty: Property | null;
  hoveredJob: Job | null;
  onPositionUpdate: (pos: {
    x: number;
    y: number;
  }) => void;
}) {
  const map = useMap();
  const updatePosition = useCallback(() => {
    let lat = 0,
      lng = 0;
    if (hoveredPackage) {
      lat = hoveredPackage.lat;
      lng = hoveredPackage.lng;
    } else if (hoveredEmergency) {
      lat = hoveredEmergency.lat;
      lng = hoveredEmergency.lng;
    } else if (hoveredProperty) {
      lat = hoveredProperty.lat;
      lng = hoveredProperty.lng;
    } else if (hoveredJob) {
      lat = hoveredJob.lat;
      lng = hoveredJob.lng;
    }
    if (lat && lng) {
      const point = map.latLngToContainerPoint([lat, lng]);
      onPositionUpdate({
        x: point.x,
        y: point.y
      });
    }
  }, [map, hoveredPackage, hoveredEmergency, hoveredProperty, hoveredJob, onPositionUpdate]);
  useEffect(() => {
    updatePosition();
  }, [updatePosition]);
  useMapEvents({
    move: updatePosition,
    zoom: updatePosition
  });
  return null;
}
// --- Main Component ---
export function UnifiedMap({
  jobs,
  packages,
  teamMembers,
  emergencyInterventions,
  activeJobId,
  activePackageId,
  selectedMemberId,
  selectedEmergencyId,
  onJobSelect,
  onPackageSelect,
  onMemberSelect,
  onEmergencyClick,
  onEmergencyPositionUpdate,
  showJobs,
  showOpportunities,
  showTeam
}: UnifiedMapProps) {
  const defaultCenter: [number, number] = [41.9028, 12.4964];
  const [currentZoom, setCurrentZoom] = useState(13);
  const [isMapMoving, setIsMapMoving] = useState(false);
  // Tooltip State
  const [hoveredJob, setHoveredJob] = useState<Job | null>(null);
  const [hoveredPackage, setHoveredPackage] = useState<PackageData | null>(null);
  const [hoveredEmergency, setHoveredEmergency] = useState<EmergencyIntervention | null>(null);
  const [hoveredProperty, setHoveredProperty] = useState<{
    property: Property;
    index: number;
  } | null>(null);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0
  });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Active items for controller
  const activePackage = packages.find(p => p.id === activePackageId) || null;
  const activeJob = jobs.find(j => j.id === activeJobId) || null;
  // Determine center and zoom target
  let center = defaultCenter;
  let targetZoom = 13;
  if (activePackage) {
    center = [activePackage.lat, activePackage.lng];
    targetZoom = 16;
  } else if (activeJob) {
    center = [activeJob.lat, activeJob.lng];
    targetZoom = 16;
  }
  const showSplitView = currentZoom >= SPLIT_ZOOM_THRESHOLD;
  // Hover Handlers
  const clearHovers = () => {
    setHoveredJob(null);
    setHoveredPackage(null);
    setHoveredEmergency(null);
    setHoveredProperty(null);
  };
  const handleHover = (item: any, type: 'job' | 'package' | 'emergency' | 'property', index?: number) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      clearHovers();
      if (type === 'job') setHoveredJob(item);
      if (type === 'package') setHoveredPackage(item);
      if (type === 'emergency') setHoveredEmergency(item);
      if (type === 'property') setHoveredProperty({
        property: item,
        index: index || 0
      });
    }, 100);
  };
  const handleMouseOut = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    clearHovers();
  };
  return <div className="h-full w-full relative overflow-hidden" data-id="element-2508">
      <MapContainer center={defaultCenter} zoom={13} scrollWheelZoom={true} zoomControl={false} style={{
      height: '100%',
      width: '100%'
    }} data-id="element-2509">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" keepBuffer={8} updateWhenZooming={false} updateWhenIdle={true} data-id="element-2510" />
        <ZoomControl position="topright" data-id="element-2511" />
        <MapResizer data-id="element-2512" />
        <ZoomTracker onZoomChange={setCurrentZoom} data-id="element-2513" />
        <MapMovementTracker onMovingChange={setIsMapMoving} data-id="element-2514" />
        <MapController center={center} zoom={targetZoom} activePackage={activePackage} activeJob={activeJob} data-id="element-2515" />
        <TooltipPositionUpdater hoveredJob={hoveredJob} hoveredPackage={hoveredPackage} hoveredEmergency={hoveredEmergency} hoveredProperty={hoveredProperty?.property || null} onPositionUpdate={setTooltipPos} data-id="element-2516" />

        {/* HQ Marker */}
        <Marker position={[41.8986, 12.4769]} icon={createHeadquarterIcon()} interactive={false} data-id="element-2517" />

        {/* --- JOBS LAYER --- */}
        {showJobs && jobs.map(job => {
        if (selectedMemberId && job.cleanerId !== selectedMemberId) return null;
        return <Marker key={`job-${job.id}`} position={[job.lat, job.lng]} icon={createJobMarkerIcon(job.status, job.id === activeJobId)} zIndexOffset={job.status === 'in-progress' ? 500 : job.id === activeJobId ? 400 : 0} eventHandlers={{
          click: e => {
            L.DomEvent.stopPropagation(e.originalEvent);
            onJobSelect(job.id);
          },
          mouseover: () => handleHover(job, 'job'),
          mouseout: handleMouseOut
        }} data-id="element-2518" />;
      })}

        {/* --- TEAM LAYER --- */}
        {showTeam && teamMembers.map(member => {
        if (selectedMemberId && selectedMemberId !== member.id) return null;
        return <Marker key={`member-${member.id}`} position={[member.currentLat, member.currentLng]} icon={createTeamMemberIcon(member, selectedMemberId === member.id)} zIndexOffset={1000} eventHandlers={{
          click: e => {
            L.DomEvent.stopPropagation(e.originalEvent);
            onMemberSelect(selectedMemberId === member.id ? null : member.id);
          }
        }} data-id="element-2519" />;
      })}

        {/* --- OPPORTUNITIES LAYER --- */}
        {showOpportunities && <>
            {/* Emergencies */}
            {emergencyInterventions.map(intervention => <Marker key={`emergency-${intervention.id}`} position={[intervention.lat, intervention.lng]} icon={createEmergencyIcon()} eventHandlers={{
          mouseover: () => handleHover(intervention, 'emergency'),
          mouseout: handleMouseOut,
          click: e => {
            L.DomEvent.stopPropagation(e.originalEvent);
            onEmergencyClick(intervention.id);
          }
        }} data-id="element-2520" />)}

            {/* Packages */}
            {packages.map(pkg => {
          const isActive = pkg.id === activePackageId;
          const shouldSplit = isActive && showSplitView;
          if (shouldSplit) {
            const radius = computeBoundingCircle(pkg.properties, pkg.lat, pkg.lng);
            return <Fragment key={`pkg-split-${pkg.id}`} data-id="element-2521">
                    {!isMapMoving && <Circle center={[pkg.lat, pkg.lng]} radius={radius} pathOptions={{
                color: '#2E8C7E',
                fillColor: '#2E8C7E',
                fillOpacity: 0.12,
                weight: 2,
                opacity: 0.4
              }} data-id="element-2522" />}
                    {pkg.properties.map((prop, idx) => <Marker key={`prop-${prop.id}`} position={[prop.lat, prop.lng]} icon={createPropertyDotIcon(idx + 1)} eventHandlers={{
                click: () => onPackageSelect(pkg.id),
                mouseover: () => handleHover(prop, 'property', idx + 1),
                mouseout: handleMouseOut
              }} data-id="element-2523" />)}
                  </Fragment>;
          }
          return <Marker key={`pkg-${pkg.id}`} position={[pkg.lat, pkg.lng]} icon={createZoneIcon(pkg.count, isActive)} eventHandlers={{
            click: () => onPackageSelect(pkg.id),
            mouseover: () => handleHover(pkg, 'package'),
            mouseout: handleMouseOut
          }} data-id="element-2524" />;
        })}
          </>}

        {/* --- ROUTES (when team member selected) --- */}
        {selectedMemberId && jobs.filter(j => j.cleanerId === selectedMemberId && j.status === 'in-progress').map(job => {
        const member = teamMembers.find(m => m.id === selectedMemberId);
        if (!member) return null;
        return <Polyline key={`route-${member.id}-${job.id}`} positions={[[member.currentLat, member.currentLng], [job.lat, job.lng]]} pathOptions={{
          color: '#1E365C',
          weight: 2.5,
          opacity: 0.6,
          dashArray: '0'
        }} data-id="element-2525" />;
      })}
      </MapContainer>

      {/* Tooltips */}
      {hoveredJob && <JobHoverTooltip job={hoveredJob} position={tooltipPos} visible={!!hoveredJob} data-id="element-2526" />}
      {hoveredPackage && <PackageHoverTooltip pkg={hoveredPackage} position={tooltipPos} visible={!!hoveredPackage} data-id="element-2527" />}
      {hoveredEmergency && <EmergencyHoverTooltip intervention={hoveredEmergency} position={tooltipPos} visible={!!hoveredEmergency} data-id="element-2528" />}
      {hoveredProperty && <PropertyHoverTooltip property={hoveredProperty.property} index={hoveredProperty.index} position={tooltipPos} visible={!!hoveredProperty} data-id="element-2529" />}
    </div>;
}